import { useEffect, useState } from "react";
import { CreateUserForm } from "../models/adminUsers.model";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Button } from "primereact/button";
import { createUserService } from "../services/adminUser.service";
import { roles } from "../../../../../utilities/dropdownDataBase";
import { ToastControlBackendModel } from "../../../../../components/Sidebar/ToastBackControl/models/toastBackControl.model";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";

interface Props {
  getAllUsersData: () => void;
  setMessageBackend: (value: ToastControlBackendModel) => void;
}

function FormCreateUser({
  getAllUsersData,
  setMessageBackend,
}: Props): JSX.Element {
  const [user, setUser] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [role, setRole] = useState<string>("");
  const [disabledButton, setDisabledButton] = useState<boolean>(true);
  const [balance, setBalance] = useState<number>(0);

  useEffect(() => {
    if (user && password && name && lastName && phone && role) {
      setDisabledButton(false);
    } else {
      setDisabledButton(true);
    }
  }, [role, user, password, name, lastName, phone]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDisabledButton(true);
    try {
      const data: CreateUserForm = {
        user,
        password,
        name,
        lastName,
        phone,
        role,
        balance,
      };
      const response = await createUserService(data);
      if (response.statusCode < 299) {
        getAllUsersData();
        setMessageBackend({
          detailValue: "Usuario creado con éxito",
          severityValue: "success",
          lifeValue: 3000,
          validateShowMessage: true,
        });
      }
    } catch (error) {
      setMessageBackend({
        detailValue: "Ocurrió un error al crear el usuario",
        severityValue: "error",
        lifeValue: 3000,
        validateShowMessage: true,
      });
    } finally {
      setDisabledButton(false);
    }
  };

  const handleBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === "") {
      setBalance(0);
      return;
    }
    const cleanVal = val.replace(/[^0-9.]/g, "");
    setBalance(Number(cleanVal));
  };

  const getInputStyle = (value: string | number) => ({
    backgroundColor: value ? "#ffffff" : "#f1f5f9",
    borderColor: value ? "#64748b" : "#cbd5e1",
    color: "#334155",
    transition: "all 0.2s ease"
  });

  return (
    <div className="px-1 py-1">
      <div 
        className="p-4 md:p-5 border-round-xl shadow-1 border-1" 
        style={{ backgroundColor: "#f8fafc", borderColor: "#e2e8f0" }}
      >
        <div className="mb-4 pb-3 border-bottom-1" style={{ borderColor: "#e2e8f0" }}>
          <h3 className="text-xl font-bold m-0" style={{ color: "#334155" }}>
            Datos del Nuevo Usuario
          </h3>
          <p className="text-xs text-slate-500 m-0 mt-1">
            Complete todos los campos obligatorios para registrar la cuenta en el sistema.
          </p>
        </div>

        <form className="grid" onSubmit={onSubmit}>
          
          <div className="col-12 md:col-6 flex flex-column gap-1.5 mb-2">
            <label className="text-xs font-semibold" style={{ color: "#475569" }}>Usuario *</label>
            <IconField iconPosition="left">
              <InputIcon className="pi pi-user text-sm" style={{ color: "#383f47ff" }} />
              <InputText
                autoFocus
                maxLength={100}
                minLength={2}
                className="w-full text-left p-inputtext-sm"
                style={getInputStyle(user)}
                type="text"
                placeholder="Ej. juan.perez"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUser(e.target.value)}
              />
            </IconField>
          </div>

          <div className="col-12 md:col-6 flex flex-column gap-1.5 mb-2">
            <label className="text-xs font-semibold" style={{ color: "#475569" }}>Contraseña *</label>
            <IconField iconPosition="left">
              <InputIcon className="pi pi-lock text-sm" style={{ color: "#383f47ff" }} />
              <InputText
                maxLength={100}
                minLength={2}
                className="w-full text-left p-inputtext-sm"
                style={getInputStyle(password)}
                type="password"
                placeholder="••••••••"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              />
            </IconField>
          </div>

          <div className="col-12 md:col-6 flex flex-column gap-1.5 mb-2">
            <label className="text-xs font-semibold" style={{ color: "#383f47ff" }}>Nombre *</label>
            <InputText
              maxLength={100}
              minLength={2}
              className="w-full text-left p-inputtext-sm"
              style={getInputStyle(name)}
              type="text"
              placeholder="Ej. Juan"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </div>

          <div className="col-12 md:col-6 flex flex-column gap-1.5 mb-2">
            <label className="text-xs font-semibold" style={{ color: "#383f47ff" }}>Apellido *</label>
            <InputText
              maxLength={100}
              minLength={2}
              className="w-full text-left p-inputtext-sm"
              style={getInputStyle(lastName)}
              type="text"
              placeholder="Ej. Pérez"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
            />
          </div>

          <div className="col-12 md:col-6 flex flex-column gap-1.5 mb-2">
            <label className="text-xs font-semibold" style={{ color: "#475569" }}>Teléfono *</label>
            <IconField iconPosition="left">
              <InputIcon className="pi pi-phone text-sm" style={{ color: "#383f47ff" }} />
              <InputText
                maxLength={20}
                minLength={2}
                className="w-full text-left p-inputtext-sm"
                style={getInputStyle(phone)}
                type="text"
                placeholder="Ej. +34 600 000 000"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
              />
            </IconField>
          </div>

          <div className="col-12 md:col-6 flex flex-column gap-1.5 mb-2">
            <label className="text-xs font-semibold" style={{ color: "#383f47ff" }}>Rol del Sistema *</label>
            <Dropdown
              className="w-full text-left p-inputtext-sm"
              style={getInputStyle(role)}
              placeholder="Seleccione un rol"
              value={role}
              options={roles}
              optionLabel="name"
              optionValue="code"
              onChange={(e: DropdownChangeEvent) => setRole(e.target.value)}
              panelStyle={{ backgroundColor: "#f8fafc", border: "1px solid #cbd5e1" }}
              pt={{
                input: { style: { color: "#334155" } },
                item: (options: any) => ({
                  style: {
                    color: "#334155",
                    backgroundColor: options?.context?.selected || options?.selected ? "#e2e8f0" : options?.context?.focused || options?.focused ? "#f1f5f9" : "transparent",
                    fontSize: "0.875rem"
                  }
                })
              }}
            />
          </div>

          <div className="col-12 md:col-6 flex flex-column gap-1.5 mb-4">
            <label className="text-xs font-semibold" style={{ color: "#383f47ff" }}>Saldo Inicial</label>
            <IconField iconPosition="left">
              <InputIcon className="pi pi-wallet text-sm" style={{ color: "#383f47ff" }} />
              <InputText
                type="text"
                inputMode="decimal"
                className="w-full text-left p-inputtext-sm"
                style={getInputStyle(balance || "")}
                placeholder="0.00"
                value={balance === 0 ? "" : balance.toString()}
                onChange={handleBalanceChange}
              />
            </IconField>
          </div>

          <div className="col-12 flex justify-content-end mt-2">
            <Button
              type="submit"
              disabled={disabledButton}
              label="Registrar Usuario"
              icon="pi pi-user-plus"
              className="p-button-sm px-4 py-2.5 font-semibold border-round-lg shadow-1"
              style={{ 
                backgroundColor: disabledButton ? "#cbd5e1" : "#00A86B", 
                borderColor: disabledButton ? "#cbd5e1" : "#00A86B",
                color: disabledButton ? "#94a3b8" : "#ffffff"
              }}
            />
          </div>

        </form>
      </div>
    </div>
  );
}

export default FormCreateUser;